/**
 * NPC 配置编辑器组件
 * 提供 NPC 配置的表单式编辑界面
 */
import React, { useState, useEffect } from 'react'
import {
  Card,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Space,
  Tabs,
  Tag,
  Row,
  Col,
  Divider,
  Switch,
  List,
  Avatar,
  Typography,
  Modal,
  message,
  Popconfirm
} from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  UpOutlined,
  DownOutlined,
  UserOutlined,
  RobotOutlined,
  EyeOutlined
} from '@ant-design/icons'
import type { NpcConfigData, DialogueEntry, DialogueLine, NpcRelation, NpcSchedule } from '@/types/scriptConfigBlock'

const { TextArea } = Input
const { Text, Title } = Typography
const { TabPane } = Tabs

// NPC 编辑器属性
interface NpcEditorFormProps {
  initialData?: NpcConfigData
  onSave: (data: NpcConfigData) => Promise<void>
  onPreview?: (data: NpcConfigData) => void
  loading?: boolean
}

// 对话条目编辑器
const DialogueEntryEditor: React.FC<{
  entry: DialogueEntry
  index: number
  onUpdate: (index: number, entry: DialogueEntry) => void
  onRemove: (index: number) => void
  onMoveUp: (index: number) => void
  onMoveDown: (index: number) => void
}> = ({ entry, index, onUpdate, onRemove, onMoveUp, onMoveDown }) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <Card
      size="small"
      title={`对话条目 #${index + 1}`}
      extra={
        <Space>
          <Button size="small" icon={<UpOutlined />} onClick={() => onMoveUp(index)} disabled={index === 0} />
          <Button size="small" icon={<DownOutlined />} onClick={() => onMoveDown(index)} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onRemove(index)} />
        </Space>
      }
      style={{ marginBottom: 8 }}
    >
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="触发条件">
            <Input
              value={entry.trigger || ''}
              onChange={(e) => onUpdate(index, { ...entry, trigger: e.target.value })}
              placeholder="例如: after_meet_stranger"
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="条件表达式">
            <Input
              value={entry.conditions ? JSON.stringify(entry.conditions) : ''}
              onChange={(e) => {
                try {
                  const val = JSON.parse(e.target.value || '{}')
                  onUpdate(index, { ...entry, conditions: val })
                } catch {
                  // 忽略无效 JSON
                }
              }}
              placeholder='{"var_evidence": 3}'
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="操作">
            <Button size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? '收起对话' : '展开对话'}
            </Button>
          </Form.Item>
        </Col>
      </Row>

      {expanded && (
        <>
          <Divider>对话内容</Divider>
          {entry.lines.map((line, lineIndex) => (
            <Row key={lineIndex} gutter={8} style={{ marginBottom: 8 }}>
              <Col span={5}>
                <Input
                  value={line.speaker}
                  onChange={(e) => {
                    const newLines = [...entry.lines]
                    newLines[lineIndex] = { ...line, speaker: e.target.value }
                    onUpdate(index, { ...entry, lines: newLines })
                  }}
                  placeholder="说话者"
                />
              </Col>
              <Col span={4}>
                <Input
                  value={line.emotion || ''}
                  onChange={(e) => {
                    const newLines = [...entry.lines]
                    newLines[lineIndex] = { ...line, emotion: e.target.value }
                    onUpdate(index, { ...entry, lines: newLines })
                  }}
                  placeholder="情绪"
                />
              </Col>
              <Col span={4}>
                <Input
                  value={line.action || ''}
                  onChange={(e) => {
                    const newLines = [...entry.lines]
                    newLines[lineIndex] = { ...line, action: e.target.value }
                    onUpdate(index, { ...entry, lines: newLines })
                  }}
                  placeholder="动作"
                />
              </Col>
              <Col span={9}>
                <Input
                  value={line.content}
                  onChange={(e) => {
                    const newLines = [...entry.lines]
                    newLines[lineIndex] = { ...line, content: e.target.value }
                    onUpdate(index, { ...entry, lines: newLines })
                  }}
                  placeholder="对话内容"
                />
              </Col>
              <Col span={2}>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    const newLines = entry.lines.filter((_, i) => i !== lineIndex)
                    onUpdate(index, { ...entry, lines: newLines })
                  }}
                />
              </Col>
            </Row>
          ))}
          <Button
            type="dashed"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => {
              const newLines = [...entry.lines, { speaker: '', content: '' }]
              onUpdate(index, { ...entry, lines: newLines })
            }}
          >
            添加对话
          </Button>
        </>
      )}
    </Card>
  )
}

// 关系编辑器
const RelationEditor: React.FC<{
  relation: NpcRelation
  index: number
  onUpdate: (index: number, relation: NpcRelation) => void
  onRemove: (index: number) => void
}> = ({ relation, index, onUpdate, onRemove }) => {
  return (
    <Row gutter={12} style={{ marginBottom: 8 }}>
      <Col span={7}>
        <Input
          value={relation.target_npc_key}
          onChange={(e) => onUpdate(index, { ...relation, target_npc_key: e.target.value })}
          placeholder="目标 NPC 键"
        />
      </Col>
      <Col span={5}>
        <Select
          value={relation.relation_type}
          onChange={(value) => onUpdate(index, { ...relation, relation_type: value })}
          style={{ width: '100%' }}
        >
          <Select.Option value="friend">朋友</Select.Option>
          <Select.Option value="enemy">敌人</Select.Option>
          <Select.Option value="neutral">中立</Select.Option>
          <Select.Option value="rival">竞争对手</Select.Option>
          <Select.Option value="family">家人</Select.Option>
          <Select.Option value="colleague">同事</Select.Option>
        </Select>
      </Col>
      <Col span={4}>
        <InputNumber
          value={relation.initial_value}
          onChange={(value) => onUpdate(index, { ...relation, initial_value: value || 0 })}
          min={-100}
          max={100}
          style={{ width: '100%' }}
        />
      </Col>
      <Col span={6}>
        <Input
          value={relation.description || ''}
          onChange={(e) => onUpdate(index, { ...relation, description: e.target.value })}
          placeholder="关系描述"
        />
      </Col>
      <Col span={2}>
        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onRemove(index)} />
      </Col>
    </Row>
  )
}

// 日程编辑器
const ScheduleEditor: React.FC<{
  schedule: NpcSchedule
  onChange: (schedule: NpcSchedule) => void
}> = ({ schedule, onChange }) => {
  const timeSlots = [
    { key: 'morning', label: '早晨 (6:00-10:00)' },
    { key: 'noon', label: '中午 (10:00-14:00)' },
    { key: 'afternoon', label: '下午 (14:00-18:00)' },
    { key: 'evening', label: '傍晚 (18:00-22:00)' },
    { key: 'night', label: '夜晚 (22:00-2:00)' },
    { key: 'latenight', label: '深夜 (2:00-6:00)' },
  ]

  return (
    <div>
      {timeSlots.map(({ key, label }) => {
        const slotData = schedule[key as keyof NpcSchedule] as any
        return (
          <Card size="small" key={key} title={label} style={{ marginBottom: 8 }}>
            <Row gutter={12}>
              <Col span={8}>
                <Input
                  value={slotData?.location || ''}
                  onChange={(e) => {
                    onChange({
                      ...schedule,
                      [key]: { ...slotData, location: e.target.value }
                    })
                  }}
                  placeholder="地点"
                />
              </Col>
              <Col span={10}>
                <Input
                  value={slotData?.activity || ''}
                  onChange={(e) => {
                    onChange({
                      ...schedule,
                      [key]: { ...slotData, activity: e.target.value }
                    })
                  }}
                  placeholder="活动"
                />
              </Col>
              <Col span={6}>
                <Switch
                  checked={slotData?.available || false}
                  onChange={(checked) => {
                    onChange({
                      ...schedule,
                      [key]: { ...slotData, available: checked }
                    })
                  }}
                  checkedChildren="可见"
                  unCheckedChildren="隐藏"
                />
              </Col>
            </Row>
          </Card>
        )
      })}
    </div>
  )
}

// 主组件
const NpcEditorForm: React.FC<NpcEditorFormProps> = ({
  initialData,
  onSave,
  onPreview,
  loading = false
}) => {
  const [form] = Form.useForm()
  const [dialogues, setDialogues] = useState<DialogueEntry[]>([])
  const [relations, setRelations] = useState<NpcRelation[]>([])
  const [schedule, setSchedule] = useState<NpcSchedule>({})
  const [behaviors, setBehaviors] = useState<string[]>([])
  const [availableLocations, setAvailableLocations] = useState<string[]>([])
  const [activeTab, setActiveTab] = useState('basic')
  const [saving, setSaving] = useState(false)

  // 初始化表单数据
  useEffect(() => {
    if (initialData) {
      // 处理 personality 和 appearance：如果是对象，转换为格式化的 JSON 字符串
      const normalizeField = (value: any): string => {
        if (!value) return ''
        if (typeof value === 'object') {
          return JSON.stringify(value, null, 2)
        }
        return String(value)
      }

      form.setFieldsValue({
        key: initialData.key,
        name: initialData.name,
        role: initialData.role,
        gender: initialData.gender,
        age: initialData.age,
        personality: normalizeField(initialData.personality),
        background: initialData.background,
        appearance: normalizeField(initialData.appearance),
        voice_style: initialData.voice_style,
        initial_memory: initialData.initial_memory,
        system_prompt: initialData.system_prompt,
        ai_model_id: initialData.ai_model_id,
        temperature: initialData.temperature,
        max_tokens: initialData.max_tokens,
      })
      setDialogues(initialData.dialogues || [])
      setRelations(initialData.relations || [])
      setSchedule(initialData.schedule || {})
      setBehaviors(initialData.behaviors || [])
      setAvailableLocations(initialData.available_locations || [])
    } else {
      form.resetFields()
      setDialogues([])
      setRelations([])
      setSchedule({})
      setBehaviors([])
      setAvailableLocations([])
    }
  }, [initialData, form])

  // 保存处理
  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      setSaving(true)

      // 处理 personality 和 appearance：尝试解析 JSON，失败则保持字符串
      const parseField = (value: any): any => {
        if (!value) return value
        if (typeof value === 'string' && value.trim().startsWith('{')) {
          try {
            return JSON.parse(value)
          } catch {
            // JSON 解析失败，保持原字符串
            return value
          }
        }
        return value
      }

      const npcData: NpcConfigData = {
        key: values.key,
        name: values.name,
        role: values.role,
        gender: values.gender,
        age: values.age,
        personality: parseField(values.personality),
        background: values.background,
        appearance: parseField(values.appearance),
        voice_style: values.voice_style,
        initial_memory: values.initial_memory,
        system_prompt: values.system_prompt,
        ai_model_id: values.ai_model_id,
        temperature: values.temperature,
        max_tokens: values.max_tokens,
        dialogues,
        relations,
        schedule,
        behaviors,
        available_locations: availableLocations,
      }

      await onSave(npcData)
    } catch (error) {
      console.error('表单验证失败:', error)
    } finally {
      setSaving(false)
    }
  }

  // 对话操作
  const addDialogue = () => {
    setDialogues([
      ...dialogues,
      { id: `dialogue_${Date.now()}`, lines: [{ speaker: '', content: '' }] }
    ])
  }

  const updateDialogue = (index: number, entry: DialogueEntry) => {
    const newDialogues = [...dialogues]
    newDialogues[index] = entry
    setDialogues(newDialogues)
  }

  const removeDialogue = (index: number) => {
    setDialogues(dialogues.filter((_, i) => i !== index))
  }

  const moveDialogue = (index: number, direction: 'up' | 'down') => {
    const newDialogues = [...dialogues]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    if (targetIndex >= 0 && targetIndex < dialogues.length) {
      [newDialogues[index], newDialogues[targetIndex]] = [newDialogues[targetIndex], newDialogues[index]]
      setDialogues(newDialogues)
    }
  }

  // 关系操作
  const addRelation = () => {
    setRelations([
      ...relations,
      { target_npc_key: '', relation_type: 'neutral', initial_value: 0 }
    ])
  }

  const updateRelation = (index: number, relation: NpcRelation) => {
    const newRelations = [...relations]
    newRelations[index] = relation
    setRelations(newRelations)
  }

  const removeRelation = (index: number) => {
    setRelations(relations.filter((_, i) => i !== index))
  }

  // 行为标签操作
  const addBehavior = (behavior: string) => {
    if (behavior && !behaviors.includes(behavior)) {
      setBehaviors([...behaviors, behavior])
    }
  }

  const removeBehavior = (behavior: string) => {
    setBehaviors(behaviors.filter(b => b !== behavior))
  }

  // 可用地点操作
  const addLocation = (location: string) => {
    if (location && !availableLocations.includes(location)) {
      setAvailableLocations([...availableLocations, location])
    }
  }

  const removeLocation = (location: string) => {
    setAvailableLocations(availableLocations.filter(l => l !== location))
  }

  return (
    <Card
      title={
        <Space>
          <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
          <span>{initialData?.name || '新建 NPC'}</span>
          {initialData?.key && <Text code style={{ fontSize: 12 }}>{initialData.key}</Text>}
        </Space>
      }
      extra={
        <Space>
          {onPreview && (
            <Button icon={<EyeOutlined />} onClick={() => {
              form.validateFields().then(values => {
                onPreview({ ...initialData, ...values, dialogues, relations, schedule, behaviors, available_locations: availableLocations } as NpcConfigData)
              })
            }}>
              预览
            </Button>
          )}
          <Button type="primary" icon={<RobotOutlined />} onClick={handleSave} loading={saving || loading}>
            保存
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="基本信息" key="basic">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="key" label="NPC 键" rules={[{ required: true }]}>
                  <Input placeholder="例如: mysterious_stranger" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="name" label="名称" rules={[{ required: true }]}>
                  <Input placeholder="例如: 神秘陌生人" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="role" label="角色" rules={[{ required: true }]}>
                  <Input placeholder="例如: 关键证人" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="gender" label="性别">
                  <Select placeholder="选择性别">
                    <Select.Option value="male">男</Select.Option>
                    <Select.Option value="female">女</Select.Option>
                    <Select.Option value="other">其他</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="age" label="年龄">
                  <Input placeholder="例如: 35岁" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="voice_style" label="语音风格">
                  <Input placeholder="例如: 低沉、沙哑" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="personality" label="性格特征">
              <TextArea rows={2} placeholder="描述 NPC 的性格特点..." />
            </Form.Item>

            <Form.Item name="appearance" label="外貌描述">
              <TextArea rows={2} placeholder="描述 NPC 的外貌特征..." />
            </Form.Item>

            <Form.Item name="background" label="背景故事">
              <TextArea rows={4} placeholder="描述 NPC 的背景故事..." />
            </Form.Item>

            <Form.Item label="行为标签">
              <Select
                mode="tags"
                value={behaviors}
                onChange={setBehaviors}
                placeholder="添加行为标签"
                style={{ width: '100%' }}
              >
                <Select.Option value="aggressive">激进</Select.Option>
                <Select.Option value="friendly">友好</Select.Option>
                <Select.Option value="secretive">神秘</Select.Option>
                <Select.Option value="helpful">乐于助人</Select.Option>
                <Select.Option value="hostile">敌对</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item label="可用地点">
              <Select
                mode="tags"
                value={availableLocations}
                onChange={setAvailableLocations}
                placeholder="添加 NPC 会出现的地方"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </TabPane>

          <TabPane tab="AI 配置" key="ai">
            <Form.Item name="system_prompt" label="系统提示词">
              <TextArea rows={6} placeholder="定义 NPC 的行为方式和对话风格..." />
            </Form.Item>

            <Form.Item name="initial_memory" label="初始记忆">
              <TextArea rows={3} placeholder="NPC 初始状态下拥有的记忆..." />
            </Form.Item>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="ai_model_id" label="AI 模型 ID">
                  <Input placeholder="例如: doubao-seed-evolving" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="temperature" label="温度参数">
                  <InputNumber min={0} max={2} step={0.1} precision={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="max_tokens" label="最大 Token">
                  <InputNumber min={100} max={8192} step={100} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Card size="small" title="AI 参数说明" style={{ marginTop: 16 }}>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li><Text strong>温度 (temperature)</Text>: 控制回复的随机性，0.7 为平衡值</li>
                <li><Text strong>最大 Token</Text>: 单次回复的最大长度限制</li>
                <li><Text strong>AI 模型</Text>: 使用的语言模型，默认为 doubao-seed-evolving</li>
              </ul>
            </Card>
          </TabPane>

          <TabPane tab={`对话 (${dialogues.length})`} key="dialogues">
            <div style={{ marginBottom: 16 }}>
              <Button type="dashed" icon={<PlusOutlined />} onClick={addDialogue}>
                添加对话
              </Button>
            </div>

            {dialogues.map((entry, index) => (
              <DialogueEntryEditor
                key={entry.id || index}
                entry={entry}
                index={index}
                onUpdate={updateDialogue}
                onRemove={removeDialogue}
                onMoveUp={(i) => moveDialogue(i, 'up')}
                onMoveDown={(i) => moveDialogue(i, 'down')}
              />
            ))}

            {dialogues.length === 0 && (
              <Card size="small" style={{ textAlign: 'center' }}>
                <Text type="secondary">暂无对话内容，点击上方按钮添加</Text>
              </Card>
            )}
          </TabPane>

          <TabPane tab={`关系 (${relations.length})`} key="relations">
            <div style={{ marginBottom: 16 }}>
              <Button type="dashed" icon={<PlusOutlined />} onClick={addRelation}>
                添加关系
              </Button>
            </div>

            <Row gutter={8} style={{ marginBottom: 8 }}>
              <Col span={7}><Text strong>目标 NPC</Text></Col>
              <Col span={5}><Text strong>关系类型</Text></Col>
              <Col span={4}><Text strong>初始值</Text></Col>
              <Col span={6}><Text strong>描述</Text></Col>
              <Col span={2}></Col>
            </Row>

            {relations.map((relation, index) => (
              <RelationEditor
                key={index}
                relation={relation}
                index={index}
                onUpdate={updateRelation}
                onRemove={removeRelation}
              />
            ))}

            {relations.length === 0 && (
              <Card size="small" style={{ textAlign: 'center' }}>
                <Text type="secondary">暂无关系配置，点击上方按钮添加</Text>
              </Card>
            )}
          </TabPane>

          <TabPane tab="日程安排" key="schedule">
            <ScheduleEditor schedule={schedule} onChange={setSchedule} />
          </TabPane>
        </Tabs>
      </Form>
    </Card>
  )
}

export default NpcEditorForm
